<template>
  <v-dialog v-model="dialog" width="720" @click:outside="cancel" :persistent="importing">
    <v-card>
      <v-card-title>
        <h3><strong>{{ $t('userCsvImportDialog.label.title') }}</strong></h3>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12">
              <div class="text-subtitle-2 mb-1">{{ $t('userCsvImportDialog.label.file') }}</div>
              <input
                ref="fileInput"
                type="file"
                :accept="acceptedFileTypes"
                style="display: none"
                @change="onFileSelected"
              />
              <v-btn variant="outlined" :disabled="importing" @click="openFilePicker">
                {{ $t('common.action.upload') }}
              </v-btn>
              <span class="ml-3">{{ fileName }}</span>
              <div v-if="fileTypeError" class="mt-2" style="color: red">{{ fileTypeError }}</div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <!-- 受け付けるファイル形式・列の並びと必須項目・記入例（サーバー修正 S-40、2026-10-27 会議） -->
              <div class="format_box">
                <div class="text-subtitle-2 mb-1">{{ $t('userCsvImportDialog.label.formatTitle') }}</div>
                <ul class="pl-5">
                  <li>{{ $t('userCsvImportDialog.label.formatLine1') }}</li>
                  <li>{{ $t('userCsvImportDialog.label.formatLine2') }}</li>
                  <li>{{ $t('userCsvImportDialog.label.formatLine3') }}</li>
                </ul>
                <table class="columns mt-2">
                  <thead>
                    <tr>
                      <th>{{ $t('userCsvImportDialog.label.colHeader') }}</th>
                      <th>{{ $t('userCsvImportDialog.label.colRequired') }}</th>
                      <th>{{ $t('userCsvImportDialog.label.colContent') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in columns" :key="c.header">
                      <td class="nowrap">{{ c.header }}</td>
                      <td class="nowrap">{{ $t('userCsvImportDialog.required.' + c.required) }}</td>
                      <td>{{ $t('userCsvImportDialog.column.' + c.key) }}</td>
                    </tr>
                  </tbody>
                </table>
                <div class="text-caption mt-2">{{ $t('userCsvImportDialog.label.sample') }}</div>
                <pre class="csv_sample">{{ sampleCsv }}</pre>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text :disabled="importing" @click="cancel">{{ $t('common.action.cancel') }}</v-btn>
        <v-btn color="indigo darken-1" dark :disabled="!file" :loading="importing" @click="importFile">
          {{ $t('userCsvImportDialog.action.import') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { isAcceptedImportFile } from "@/utils/importFileType";

/**
 * ユーザーCSV取込（WEB-08）。取り込む前に、ファイルの形式と記入例を見せる（サーバー修正 S-40）。
 *
 * 取込そのもの（/user/loadcsv と結果の表示）はユーザー登録画面が行う。
 * 列は m_csv_setting の USER_IMPORT（見出しの名前で対応づける）に合わせてある。
 */
export default {
  props: {
    handleCancel: {
      type: Function,
      required: true,
    },
    // 「取込」を押したら、選んだファイルを渡して呼ぶ
    handleImport: {
      type: Function,
      required: true,
    },
    // 取込の送信中（閉じられないようにし、「取込」をくるくるにする。受入テスト #23）
    importing: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      dialog: true,
      file: null,
      // 受け付けるファイルの種類（input の accept と、選んだときの確認に使う）
      acceptedFileTypes: ".csv",
      fileTypeError: "",
      // 見出しは m_csv_setting（USER_IMPORT）の header_value と同じ文字にすること
      columns: [
        { header: "ログインID", required: "yes", key: "userId" },
        { header: "名前", required: "yes", key: "userName" },
        { header: "パスワード", required: "newOnly", key: "userPass" },
        { header: "ステータス", required: "yes", key: "status" },
        { header: "ロール", required: "yes", key: "role" },
        { header: "ログイン可能店舗", required: "storeOnly", key: "siteList" },
      ],
      sampleCsv: [
        "ログインID,名前,パスワード,ステータス,ロール,ログイン可能店舗",
        "10001,無印良品 銀座 店舗,abcd1234,0,2,10001",
        "honbu01,本部 管理者,abcd1234,0,0,",
      ].join("\n"),
    };
  },

  computed: {
    fileName() {
      return this.file ? this.file.name : "";
    },
  },

  methods: {
    cancel() {
      if (this.importing) return;
      this.handleCancel();
    },
    openFilePicker() {
      this.$refs.fileInput.click();
    },
    onFileSelected() {
      const file = this.$refs.fileInput.files[0] ?? null;
      this.fileTypeError = "";
      // 「すべてのファイル」に切り替えると CSV 以外も選べるので、選んだ時点で確かめて送らない
      if (file && !isAcceptedImportFile(file, this.acceptedFileTypes)) {
        this.file = null;
        this.$refs.fileInput.value = null;
        this.fileTypeError = this.$t("common.message.selectCsvFile");
        return;
      }
      this.file = file;
    },
    importFile() {
      if (!this.file || this.importing) return;
      this.handleImport(this.file);
    },
  },
};
</script>

<style scoped>
.format_box {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fafafa;
  font-size: 13px;
}
.columns {
  border-collapse: collapse;
  font-size: 13px;
}
.columns th,
.columns td {
  border: 1px solid #ccc;
  padding: 2px 8px;
  text-align: left;
  vertical-align: top;
}
.columns th {
  background: #eee;
}
.nowrap {
  white-space: nowrap;
}
.csv_sample {
  margin: 0;
  padding: 4px 8px;
  font-size: 13px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  white-space: pre;
}
</style>
